import { AppMark } from "../components/AppMark";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { useOrgSettingsQuery } from "../hooks/useUnits";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { InviteSheet } from "../components/InviteSheet";
import { BottomNav } from "../components/BottomNav";
import type { ApiTokenRow, BillingResponse, ConsoleContext, Player } from "../lib/types";
import { formatMoney } from "../lib/engage";
import { clearAuth, getCurrentOrg } from "../lib/storage";

// Piece-rate pay (GET/PUT /api/pay, owner-gated like /api/console). Stored
// as whole cents per gram; the input is in currency units per gram since
// that's how a rate is quoted on the trim-room wall ("$0.50/g").
const PAY_CURRENCIES = ["NZD", "AUD", "USD", "CAD", "EUR", "GBP"] as const;
type PaySettings = { rate_cents_per_g: number | null; currency: string };

// Owner console — ported from legacy templates/cloud_console.html, minus
// the server-rendered QR image (v5 has no QR endpoint; a copyable link is
// the sanctioned alternative per the brief) and the join-link "rotate code"
// action (not exposed by any v5 API route — out of scope, unlike the
// harvest-toggle gap this page also carries, rotation was never asked for).
// GET /api/console is a new minimal JSON companion to org-routes.ts's
// existing SSR /console page (same owner-session gate, same org-rules.ts
// helpers) — additive only, the SSR page and its contract-pinned strings
// are untouched.

function planPill(ctx: ConsoleContext): string {
  if (ctx.org.plan === "self_hosted") return "Free self-hosting · unlimited crew";
  if (ctx.org.plan === "solo") return "Solo";
  if (ctx.org.plan === "active") return `Active · ${ctx.org.paid_seats} seat${ctx.org.paid_seats === 1 ? "" : "s"}`;
  if (ctx.org.plan === "trial" && ctx.locked) return "Trial ended";
  if (ctx.org.plan === "trial") return `${ctx.days_left} day${ctx.days_left === 1 ? "" : "s"} left`;
  return "Cancelled";
}

export function ConsolePage() {
  const { isOwner, loading: authLoading } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [showNewToken, setShowNewToken] = useState(false);
  const [tokenName, setTokenName] = useState("");
  const [tokenScope, setTokenScope] = useState<"read" | "write">("read");
  const [mintedToken, setMintedToken] = useState<string | null>(null);
  const [creatingToken, setCreatingToken] = useState(false);
  const [changingCrew, setChangingCrew] = useState<number | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [currencyInput, setCurrencyInput] = useState<string>("NZD");
  const [savingPay, setSavingPay] = useState(false);
  const [savingLeaderboard, setSavingLeaderboard] = useState(false);
  const [newCrewName, setNewCrewName] = useState("");
  const [addingCrew, setAddingCrew] = useState(false);
  const [inviting, setInviting] = useState<{ id: number; name: string } | null>(null);
  const { data: orgSettings } = useOrgSettingsQuery();

  const { data: ctx, isLoading: ctxLoading } = useQuery({
    queryKey: ["console-context"],
    queryFn: () => api.get("/api/console").then((r) => readJson<ConsoleContext>(r)),
    enabled: isOwner,
  });
  // Only needed for the trial-ending banner: /api/console has days_left but
  // not the actual end date.
  const { data: billing } = useQuery({
    queryKey: ["billing"],
    queryFn: () => api.get("/api/billing").then((r) => readJson<BillingResponse>(r)),
    enabled: isOwner && ctx?.org.plan === "trial" && !ctx.locked,
    retry: false,
  });
  const trialEndsAt = billing?.trial_ends_at ?? null;
  const trialEndingSoon = ctx?.org.plan === "trial" && !ctx.locked && trialEndsAt !== null
    && trialEndsAt * 1000 > Date.now() && trialEndsAt * 1000 - Date.now() <= 2 * 86_400_000;
  const { data: crew } = useQuery({
    queryKey: ["console-crew"],
    queryFn: () => api.get("/api/players?include_inactive=1").then((r) => readJson<Player[]>(r)),
    enabled: isOwner,
  });
  const { data: tokens } = useQuery({
    queryKey: ["api-tokens"],
    queryFn: () => api.get("/api/tokens").then((r) => readJson<ApiTokenRow[]>(r)),
    enabled: isOwner,
  });

  const { data: pay } = useQuery({
    queryKey: ["pay"],
    queryFn: () => api.get("/api/pay").then((r) => readJson<PaySettings>(r)),
    enabled: isOwner,
    retry: false,
  });
  useEffect(() => {
    if (pay) setCurrencyInput(pay.currency);
  }, [pay]);

  async function savePay(rateCents: number | null) {
    setSavingPay(true);
    const res = await api.putRaw("/api/pay", JSON.stringify({ rate_cents_per_g: rateCents, currency: currencyInput }), {
      "Content-Type": "application/json",
    });
    setSavingPay(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not save piece rate", "err"); return; }
    toast(rateCents === null ? "Piece rate turned off" : "Piece rate saved", "ok");
    setRateInput("");
    queryClient.invalidateQueries({ queryKey: ["pay"] });
  }
  async function saveLeaderboardPublic(on: boolean) {
    setSavingLeaderboard(true);
    const res = await api.patchJson("/api/org/settings", { leaderboard_public: on });
    setSavingLeaderboard(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not save leaderboard setting", "err"); return; }
    toast(on ? "Leaderboard visible to all crew" : "Leaderboard hidden from crew", "ok");
    queryClient.invalidateQueries({ queryKey: ["org-settings"] });
  }
  async function saveSeasonCards(on: boolean) {
    setSavingLeaderboard(true);
    const res = await api.patchJson("/api/org/settings", { season_cards: on });
    setSavingLeaderboard(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not save Season Card setting", "err"); return; }
    toast(on ? "Season Cards on for crew" : "Season Cards off", "ok");
    queryClient.invalidateQueries({ queryKey: ["org-settings"] });
  }
  function savePayFromInput() {
    const perGram = Number(rateInput);
    if (!Number.isFinite(perGram) || perGram < 0) { toast("Enter a rate per gram, e.g. 0.50", "err"); return; }
    savePay(Math.round(perGram * 100));
  }

  async function startTrial() {
    const res = await api.postForm("/console/start-trial", {});
    if (res.ok) {
      toast("Team trial started", "ok");
      queryClient.invalidateQueries({ queryKey: ["console-context"] });
    } else {
      toast("Could not start trial", "err");
    }
  }

  async function changeCrewStatus(person: Player) {
    const active = person.active ? 0 : 1;
    if (!confirm(`${active ? "Restore" : "Archive"} ${person.name}? ${active ? "This uses an active crew seat." : "Their run history stays available, and they will no longer use an active seat."}`)) return;
    setChangingCrew(person.id);
    try {
      const res = await api.patchJson(`/api/players/${person.id}`, { active });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast(body.error || "Could not update crew profile", "err"); return; }
      toast(active ? "Crew profile restored" : "Crew profile archived", "ok");
      void queryClient.invalidateQueries({ queryKey: ["players"] });
      void queryClient.invalidateQueries({ queryKey: ["console-crew"] });
      void queryClient.invalidateQueries({ queryKey: ["billing"] });
    } catch { toast("Could not reach Trim Log. Try again.", "err"); }
    finally { setChangingCrew(null); }
  }

  async function addCrew(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const name = newCrewName.trim();
    if (!name) return;
    setAddingCrew(true);
    try {
      const res = await api.postForm("/api/players", { name });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast(body.error || "Could not add crew member", "err"); return; }
      setNewCrewName("");
      setInviting({ id: body.id, name: body.name });
      void queryClient.invalidateQueries({ queryKey: ["players"] });
      void queryClient.invalidateQueries({ queryKey: ["console-crew"] });
    } catch { toast("Could not reach Trim Log. Try again.", "err"); }
    finally { setAddingCrew(false); }
  }

  async function changePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (newPassword.length < 10 || newPassword.length > 128) { toast("New password needs 10–128 characters", "err"); return; }
    setSavingPassword(true);
    try {
      const res = await api.postJson("/api/account/password", { current_password: currentPassword, new_password: newPassword });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { toast(body.error || "Could not change password", "err"); return; }
      toast("Password changed", "ok");
      setCurrentPassword("");
      setNewPassword("");
    } catch { toast("Could not reach Trim Log. Try again.", "err"); }
    finally { setSavingPassword(false); }
  }

  async function signOutOwner() {
    try {
      const res = await api.postJson("/api/account/logout", {});
      if (!res.ok) { toast("Could not sign out. Try again.", "err"); return; }
      const org = getCurrentOrg();
      if (org !== null) clearAuth(org);
      window.location.assign("/site");
    } catch { toast("Could not reach Trim Log. Try again.", "err"); }
  }
  async function createToken() {
    setCreatingToken(true);
    const res = await api.postJson("/api/tokens", { name: tokenName.trim() || "token", scope: tokenScope });
    setCreatingToken(false);
    const body = await res.json().catch(() => ({}));
    if (!res.ok) { toast(body.error || "Could not create token", "err"); return; }
    setMintedToken(body.token);
    setShowNewToken(false);
    setTokenName("");
    queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
  }

  async function revokeToken(id: number) {
    if (!confirm("Revoke this token? Anything using it stops working immediately.")) return;
    const res = await api.del(`/api/tokens/${id}`);
    if (res.ok) {
      toast("Token revoked", "ok");
      queryClient.invalidateQueries({ queryKey: ["api-tokens"] });
    } else {
      toast("Revoke failed", "err");
    }
  }

  async function copyJoinUrl(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      toast("Join link copied", "ok");
    } catch {
      toast("Could not copy — select and copy manually", "warn");
    }
  }

  return (
    <>
      <header className="top-bar">
        <div className="brand"><AppMark />{ctx?.org.name || "CONSOLE"}</div>
        {isOwner && <div className="top-actions"><Link to="/billing" className="link-btn">Billing</Link><button type="button" className="link-btn" onClick={() => void signOutOwner()}>Sign out</button></div>}
      </header>
      <main className="page">
        {authLoading && <div className="muted center" style={{ paddingTop: 80 }}>Loading…</div>}

        {!authLoading && !isOwner && (
          <div className="panel center muted">
            Owner sign-in required. <Link to="/login" style={{ textDecoration: "underline" }}>Sign in to your workspace.</Link>
          </div>
        )}

        {!authLoading && isOwner && ctxLoading && (
          <div className="muted center" style={{ paddingTop: 80 }}>Loading…</div>
        )}

        {!authLoading && isOwner && ctx && (
          <>
            {trialEndingSoon && trialEndsAt !== null && (
              <div className="warn-box" data-testid="trial-ending">
                Trial ends {new Date(trialEndsAt * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} — choose seats to keep recording.{" "}
                <Link to="/billing" hash="choose-seats" style={{ textDecoration: "underline" }}>Choose seats</Link>
              </div>
            )}
            <div className="panel">
              <h2>Plan</h2>
              <span className="pill" data-testid="plan-pill">{planPill(ctx)}</span>
              {ctx.locked && (
                <div className="warn-box" style={{ marginTop: 12 }}>
                  Recording is paused. Existing records remain available to view and export. Open billing to check your access dates.
                  <div style={{ marginTop: 10 }}>
                    <Link to="/billing" hash="choose-seats" className="btn btn-primary" data-testid="choose-seats-resume">Choose seats to resume</Link>
                  </div>
                </div>
              )}
              <div className="muted small" style={{ marginTop: 10 }}>
                {ctx.org.plan === "self_hosted" ? "Free on your own infrastructure. Unlimited crew; no subscription required." : "Choose your seat count, manage PayPal payments and see your access dates."}
              </div>
              <div className="row" style={{ marginTop: 12, flexWrap: "wrap", gap: 8 }}>
                {ctx.org.plan === "solo" && <button type="button" className="btn btn-primary btn-sm" onClick={startTrial}>Start a 7-day Team trial</button>}
                <Link to="/billing" className="btn btn-primary btn-sm" data-testid="activate-billing">{ctx.org.plan === "self_hosted" ? "Hosting details" : "Manage billing"}</Link>
              </div>
            </div>
            {ctx.org.plan !== "solo" && ctx.join_url && (
              <div className="panel">
                <h2>Room screen link</h2>
                <div className="join-url" data-testid="join-url">{`${window.location.origin}${ctx.join_url}`}</div>
                <button
                  type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 10 }}
                  onClick={() => copyJoinUrl(`${window.location.origin}${ctx.join_url}`)}
                >
                  Copy link
                </button>
                <div className="muted small" style={{ marginTop: 10 }}>
                  Open this on a shared TV or tablet to show the live room. Crew don't use it to sign in:
                  each person gets their own invite from the Crew list below.
                </div>
                {ctx.seat_cap !== null && (
                  <div className="muted small" style={{ marginTop: 6 }}>Seat limit on this plan: {ctx.seat_cap}.</div>
                )}
              </div>
            )}

            <div className="panel">
              <h2>
                Piece rate{" "}
                <span className="pill" data-testid="pay-pill">
                  {pay?.rate_cents_per_g != null ? `${formatMoney(pay.rate_cents_per_g, pay.currency)}/g` : "Off"}
                </span>
              </h2>
              <div className="muted small" style={{ marginBottom: 10 }}>
                Crew see &ldquo;earned today&rdquo; on their home screen and a +$ line after every run.
                Line jobs pool the job&rsquo;s earnings and split them by station-hours. Leave off to hide money everywhere.
              </div>
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <input
                  type="number" inputMode="decimal" min={0} step="0.01" data-testid="pay-rate-input"
                  placeholder={pay?.rate_cents_per_g != null ? (pay.rate_cents_per_g / 100).toFixed(2) : "0.50"}
                  value={rateInput} onChange={(e) => setRateInput(e.target.value)} style={{ maxWidth: 120 }}
                />
                <span className="muted small">per gram</span>
                <select data-testid="pay-currency" value={currencyInput} onChange={(e) => setCurrencyInput(e.target.value)}>
                  {PAY_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  type="button" className="btn btn-primary btn-sm" data-testid="pay-save"
                  disabled={savingPay || rateInput === ""} onClick={savePayFromInput}
                >
                  Save
                </button>
                {pay?.rate_cents_per_g != null && (
                  <button type="button" className="btn btn-ghost btn-sm" data-testid="pay-off" disabled={savingPay} onClick={() => savePay(null)}>
                    Turn off
                  </button>
                )}
              </div>
            </div>

            <div className="panel">
              <h2>Leaderboard</h2>
              <label className="check-row">
                <input
                  type="checkbox" data-testid="leaderboard-public"
                  checked={orgSettings?.leaderboard_public === true}
                  disabled={savingLeaderboard || !orgSettings}
                  onChange={(e) => saveLeaderboardPublic(e.target.checked)}
                />
                <span>Show leaderboard to all crew</span>
              </label>
              <div className="muted small" style={{ marginTop: 6 }}>
                Off: only you and managers see the leaderboard and crew rankings. Crew still see their own
                stats, personal bests and streaks.
              </div>
            </div>

            <div className="panel">
              <h2>Season Cards</h2>
              <label className="check-row">
                <input
                  type="checkbox" data-testid="season-cards"
                  checked={orgSettings?.season_cards === true}
                  disabled={savingLeaderboard || !orgSettings}
                  onChange={(e) => saveSeasonCards(e.target.checked)}
                />
                <span>Let crew make a shareable Season Card</span>
              </label>
              <div className="muted small" style={{ marginTop: 6 }}>
                Each person can make an image of their own totals, pace and streak, showing this workspace's
                name, and share it. It never shows anyone else's numbers. Off by default.
              </div>
            </div>

            <div className="panel">
              <h2>API access</h2>
              {mintedToken && (
                <>
                  <div className="join-url" style={{ borderColor: "var(--primary-strong)", marginBottom: 8 }} data-testid="minted-token">
                    {mintedToken}
                  </div>
                  <div className="muted small" style={{ marginBottom: 12 }}>Copy it now — this token is not shown again.</div>
                </>
              )}
              <div className="table-wrap">
                <table className="data-table">
                  <tbody>
                    {(tokens || []).map((t) => (
                      <tr key={t.id}>
                        <td>{t.name}</td>
                        <td className="muted">{t.scope}</td>
                        <td className="muted">{t.last_used_at ? "used" : "never used"}</td>
                        <td style={{ textAlign: "right" }}>
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => revokeToken(t.id)}>Revoke</button>
                        </td>
                      </tr>
                    ))}
                    {!tokens?.length && <tr><td colSpan={4} className="muted">No tokens yet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <button
                type="button" className="btn btn-primary btn-sm" style={{ marginTop: 12 }}
                data-testid="new-token-open" onClick={() => setShowNewToken(true)}
              >
                + Create token
              </button>
              <div className="muted small" style={{ marginTop: 10 }}>
                Use as <code>Authorization: Bearer &lt;token&gt;</code>. Try <code>GET /api/summary</code> —
                made for Home Assistant REST sensors and LLM tools.
              </div>
            </div>

            <div className="panel">
              <h2>Change password</h2>
              <form onSubmit={(e) => void changePassword(e)}>
                <div className="field">
                  <label className="field-label" htmlFor="current-password">Current password</label>
                  <input id="current-password" type="password" autoComplete="current-password" maxLength={128} required value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="new-password">New password</label>
                  <input id="new-password" type="password" autoComplete="new-password" minLength={10} maxLength={128} required value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </div>
                <button type="submit" className="btn btn-primary btn-sm" data-testid="change-password" disabled={savingPassword}>
                  {savingPassword ? "Saving…" : "Change password"}
                </button>
              </form>
            </div>

            <div className="panel">
              <h2>Crew</h2>
              <form className="row" style={{ gap: 8, marginBottom: 12, flexWrap: "wrap" }} onSubmit={(e) => void addCrew(e)}>
                <input id="new-crew-name" aria-label="New crew member's name" placeholder="Name" maxLength={40} value={newCrewName} onChange={(e) => setNewCrewName(e.target.value)} style={{ flex: 1, minWidth: 160 }} />
                <button type="submit" className="btn btn-primary btn-sm" data-testid="add-crew" disabled={addingCrew || !newCrewName.trim()}>{addingCrew ? "Adding…" : "Add and invite"}</button>
              </form>
              <div className="table-wrap">
                <table className="data-table">
                  <tbody>
                    {(crew || []).map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td className="muted">{p.active ? "" : "archived"}</td>
                        <td>{p.active ? <button type="button" className="btn btn-ghost btn-sm" data-testid="invite-crew" onClick={() => setInviting(p)}>Invite</button> : null}</td>
                        <td><button type="button" className="btn btn-ghost btn-sm" disabled={changingCrew !== null} onClick={() => void changeCrewStatus(p)}>{changingCrew === p.id ? "Saving…" : p.active ? "Archive" : "Restore"}</button></td>
                      </tr>
                    ))}
                    {!crew?.length && <tr><td colSpan={4} className="muted">Nobody yet. Add a name above, then show them the QR code or send the link.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>

      {inviting && <InviteSheet person={inviting} onClose={() => setInviting(null)} />}

      {showNewToken && (
        <Modal title="Create API token" onClose={() => setShowNewToken(false)}>
          <div className="field">
            <label className="field-label" htmlFor="token-name">What&rsquo;s it for?</label>
            <input
              id="token-name" data-testid="token-name" value={tokenName} maxLength={60}
              placeholder="e.g. Home Assistant" onChange={(e) => setTokenName(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field-label" htmlFor="token-scope">Scope</label>
            <select
              id="token-scope" data-testid="token-scope" value={tokenScope}
              onChange={(e) => setTokenScope(e.target.value as "read" | "write")}
            >
              <option value="read">read only</option>
              <option value="write">read + write</option>
            </select>
          </div>
          <div className="btn-row">
            <button type="button" className="btn btn-ghost" onClick={() => setShowNewToken(false)}>Cancel</button>
            <button type="button" className="btn btn-primary" data-testid="create-token-confirm" disabled={creatingToken} onClick={createToken}>
              Create
            </button>
          </div>
        </Modal>
      )}

      <BottomNav />
    </>
  );
}
